<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Departments</h1>
        <button class="btn btn-primary" @click="openCreateModal">
          <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Department
        </button>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div class="row mb-3">
            <div class="col-md-4">
              <b-form-input
                v-model="filter"
                placeholder="Search departments..."
                debounce="300"
              ></b-form-input>
            </div>
            <div class="col-md-4">
              <b-form-select v-model="collegeFilter" @change="loadDepartments">
                <option value="">All Colleges</option>
                <option v-for="c in colleges" :key="c.id" :value="c.id">{{ c.college }}</option>
              </b-form-select>
            </div>
          </div>

          <b-table
            :items="departments"
            :fields="fields"
            :filter="filter"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading departments...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No departments found.</div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(college_name)="data">
              {{ data.item.college ? data.item.college.college : "N/A" }}
            </template>
            <template #cell(programmes_count)="data">
              <b-badge variant="info">{{ data.value }}</b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-info mr-1" @click="openEditModal(data.item)" :disabled="deleting">
                <b-icon icon="pencil"></b-icon>
              </button>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(data.item)" :disabled="deleting">
                <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center" v-if="departments.length > perPage">
            <small class="text-muted">Showing {{ departments.length }} departments</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="departments.length"
              :per-page="perPage"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Create/Edit Modal -->
      <b-modal
        v-model="showModal"
        :title="isEditing ? 'Edit Department' : 'Add Department'"
        @ok.prevent="saveDepartment"
        :ok-disabled="saving"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </template>
        <b-form @submit.prevent="saveDepartment">
          <b-form-group label="Department Name" label-for="dept-name">
            <b-form-input
              id="dept-name"
              v-model="form.department"
              placeholder="Enter department name"
              required
              :disabled="saving"
              :state="formErrors.department ? false : null"
            ></b-form-input>
            <b-form-invalid-feedback>{{ formErrors.department }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="College" label-for="dept-college">
            <b-form-select
              id="dept-college"
              v-model="form.college_id"
              :disabled="saving"
              :state="formErrors.college_id ? false : null"
            >
              <option :value="null" disabled>-- Select College --</option>
              <option v-for="c in colleges" :key="c.id" :value="c.id">{{ c.college }}</option>
            </b-form-select>
            <b-form-invalid-feedback>{{ formErrors.college_id }}</b-form-invalid-feedback>
          </b-form-group>
        </b-form>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      departments: [],
      colleges: [],
      loading: false,
      saving: false,
      deleting: false,
      deletingId: null,
      filter: "",
      collegeFilter: "",
      perPage: 20,
      currentPage: 1,
      showModal: false,
      isEditing: false,
      editId: null,
      form: { department: "", college_id: null },
      formErrors: {},
      fields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "60px" } },
        { key: "department", label: "Department Name", sortable: true },
        { key: "college_name", label: "College", sortable: true },
        { key: "programmes_count", label: "Programmes", class: "text-center", sortable: true },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "120px" } },
      ],
    };
  },
  created() {
    this.loadColleges();
    this.loadDepartments();
  },
  methods: {
    loadColleges() {
      axios
        .get("admin/colleges")
        .then((res) => {
          if (res.data.msg === "success") this.colleges = res.data.data;
        })
        .catch(() => {});
    },
    loadDepartments() {
      this.loading = true;
      let params = {};
      if (this.collegeFilter) params.college_id = this.collegeFilter;
      axios
        .get("admin/departments", { params })
        .then((res) => {
          if (res.data.msg === "success") this.departments = res.data.data;
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load departments" }))
        .finally(() => (this.loading = false));
    },
    openCreateModal() {
      this.isEditing = false;
      this.editId = null;
      this.form = { department: "", college_id: null };
      this.formErrors = {};
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditing = true;
      this.editId = item.id;
      this.form = { department: item.department, college_id: item.college_id };
      this.formErrors = {};
      this.showModal = true;
    },
    saveDepartment() {
      this.formErrors = {};
      if (!this.form.department.trim()) {
        this.formErrors.department = "Department name is required.";
        return;
      }
      if (!this.form.college_id) {
        this.formErrors.college_id = "Please select a college.";
        return;
      }
      this.saving = true;
      let request;
      if (this.isEditing) {
        request = axios.put("admin/departments/" + this.editId, this.form);
      } else {
        request = axios.post("admin/departments", this.form);
      }
      request
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: this.isEditing ? "Department updated" : "Department created" });
            this.showModal = false;
            this.loadDepartments();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Operation failed" });
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 422) {
            let data = err.response.data;
            if (data.errors) {
              let errs = {};
              Object.entries(data.errors).forEach(([k, v]) => (errs[k] = v.join(", ")));
              this.formErrors = errs;
            } else {
              Toast.fire({ icon: "error", title: data.message || "Validation error" });
            }
          } else {
            Toast.fire({ icon: "error", title: "An error occurred" });
          }
        })
        .finally(() => (this.saving = false));
    },
    confirmDelete(item) {
      Swal.fire({
        title: "Delete Department?",
        text: `Are you sure you want to delete "${item.department}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleting = true;
          this.deletingId = item.id;
          axios
            .delete("admin/departments/" + item.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Department deleted" });
                this.loadDepartments();
              } else {
                Toast.fire({ icon: "error", title: res.data.message || "Delete failed" });
              }
            })
            .catch((err) => {
              let msg = err.response?.data?.message || "Failed to delete department";
              Toast.fire({ icon: "error", title: msg });
            })
            .finally(() => {
              this.deleting = false;
              this.deletingId = null;
            });
        }
      });
    },
  },
};
</script>
